/**
 * Legal destinations referenced by the auth screens (sign-in, register, reset).
 *
 * ── Why this file exists ──────────────────────────────────────────────────
 * The auth screens make promises on the platform's behalf: the register form
 * asks a person to *agree* to Terms and a Privacy Policy, and the legal footer
 * offers to show them. A link that resolves to nothing turns that promise into
 * a lie — the user agrees to a document they provably cannot read.
 *
 * So destinations live here, in ONE place, each carrying an `href` that is
 * either a URL VERIFIED to resolve or `null`. Consumers render a real anchor
 * when there is an href and plain text when there is not; nothing in the auth
 * surface is allowed to hard-code a legal URL again.
 *
 * ── Verification log ──────────────────────────────────────────────────────
 * Checked 2026-09-06 with `nslookup` + `curl` from a shell that reached
 * `app.octopusintl.org` (200) and `example.com` (200) in the same session:
 *
 *   www.octopusintl.org          → NO DNS ANSWER (NXDOMAIN); every path fails
 *                                  to connect (curl exit 6).
 *   octopusintl.org              → resolves, but `GET /` 301s to
 *                                  `http://www.octopusintl.org` — i.e. into the
 *                                  dead host above. Effectively unreachable.
 *   octopusintl.org/terms-of-service → 404
 *   octopusintl.org/privacy-policy   → 404
 *
 * `https://www.octopusintl.org/terms-of-service` and
 * `https://www.octopusintl.org/privacy-policy` were hard-coded into the
 * register form's consent notice. They have never been reachable from here.
 * They are now routed through this registry and render as plain text until the
 * documents are actually hosted.
 *
 * ── Destinations deliberately NOT invented ────────────────────────────────
 * `accessibility` — an Accessibility link is a conformance CLAIM. This app has
 *   no accessibility statement, no automated a11y tooling in `package.json`,
 *   and `.eslintrc.yml` does not even load `eslint-plugin-jsx-a11y` (it only
 *   switches off `jsx-a11y/click-events-have-key-events`). Publishing the link
 *   would assert a commitment that does not exist.
 * `cookies` — an "Advertising & Cookies" link should only appear where such
 *   cookies are actually set. This app ships no analytics, tag manager, or ad
 *   pixel (no gtag / GTM / Segment / Mixpanel / PostHog / Hotjar / Meta pixel,
 *   and no cookie library in `package.json`); the session is a JWT in
 *   `localStorage`, and the only cookie code in `src/` is a utility that
 *   *clears* cookies. Claiming a cookie regime we do not operate is as wrong as
 *   hiding one we do.
 *
 * To publish any of these: host the document, verify the URL resolves, then set
 * its `href` below. Every auth surface picks it up with no further edits.
 */

export type LegalDocumentKey =
  | 'terms'
  | 'privacy'
  | 'accessibility'
  | 'cookies';

export type LegalDestination = {
  /** Human-readable name, used as the link text and in the consent notice. */
  readonly label: string;
  /**
   * Absolute URL, or `null` when the document is not hosted anywhere that
   * resolves. NEVER put an unverified URL here.
   */
  readonly href: string | null;
};

export const AUTH_LEGAL_DESTINATIONS: Readonly<
  Record<LegalDocumentKey, LegalDestination>
> = {
  terms: { label: 'Terms of Use', href: null },
  privacy: { label: 'Privacy Policy', href: null },
  accessibility: { label: 'Accessibility', href: null },
  cookies: { label: 'Cookies', href: null }
};

/** Display order for the footer's link row. */
const FOOTER_ORDER: readonly LegalDocumentKey[] = [
  'terms',
  'privacy',
  'accessibility',
  'cookies'
];

export type ResolvedLegalDestination = LegalDestination & {
  readonly key: LegalDocumentKey;
  readonly href: string;
};

/**
 * The destinations that actually resolve, in footer order. Returns `[]` while
 * nothing is hosted — callers must render no link row rather than dead links.
 */
export const liveLegalDestinations = (): ResolvedLegalDestination[] =>
  FOOTER_ORDER.map((key) => ({ key, ...AUTH_LEGAL_DESTINATIONS[key] })).filter(
    (destination): destination is ResolvedLegalDestination =>
      typeof destination.href === 'string' && destination.href.length > 0
  );

export const legalDestination = (key: LegalDocumentKey): LegalDestination =>
  AUTH_LEGAL_DESTINATIONS[key];
